import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { bolusnotesApi } from '../services/api';
import { useAuth } from './KStackAuthContext';

interface UserProfile {
  id: string;
  name: string;
  is_active: boolean;
  user_type?: string;
  profile_image_url?: string;
  target_blood_glucose?: number;
  has_cgm_enabled?: boolean;
}

interface ProfileContextType {
  profiles: UserProfile[];
  selectedProfile: UserProfile | null;
  setSelectedProfile: (profile: UserProfile | null) => void;
  loading: boolean;
  refreshProfiles: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [selectedProfile, setSelectedProfileState] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshProfiles = async () => {
    try {
      setLoading(true);
      const data = await bolusnotesApi.profiles.list();
      setProfiles(data);
      
      // Auto-select first profile if none selected or if previously selected is gone
      if (data.length > 0) {
        const storedProfileId = localStorage.getItem('bolusnotes_selected_profile_id');
        const found = data.find((p: UserProfile) => p.id === storedProfileId);
        if (found) {
          setSelectedProfileState(found);
        } else {
          setSelectedProfileState(data[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch profiles:', err);
    } finally {
      setLoading(false);
    }
  };

  const setSelectedProfile = (profile: UserProfile | null) => {
    setSelectedProfileState(profile);
    if (profile) {
      localStorage.setItem('bolusnotes_selected_profile_id', profile.id);
    } else {
      localStorage.removeItem('bolusnotes_selected_profile_id');
    }
  };

  useEffect(() => {
    if (user) {
      refreshProfiles();
    } else {
      setProfiles([]);
      setSelectedProfileState(null);
      setLoading(false);
    }
  }, [user]);

  return (
    <ProfileContext.Provider value={{ profiles, selectedProfile, setSelectedProfile, loading, refreshProfiles }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
